/**
 * Field Engine — Pure functions for deriving UI descriptors from field definitions.
 *
 * All functions are pure (no React hooks, no side effects).
 * `code` and `name` fields are ALWAYS sortable regardless of the `sortable` flag.
 *
 * @module fieldEngine
 * @see FMPV-MVWC-E001-S004
 */

import type {
  MasterPatternViewDefinition,
  MasterPatternViewFieldDefinition,
  MasterPatternViewInputType,
  MasterPatternViewLookupConfig,
  FilterOperator,
  SelectOption,
} from '../types';

/**
 * Returns the flat list of all field definitions from a definition object.
 *
 * Handles both shapes:
 *  - `sections` present → flattens all section fields into one array
 *  - `fields` present   → returns it directly (backward compat)
 *
 * Use this everywhere instead of `definition.fields` directly.
 */
export function getAllFields(
  definition: MasterPatternViewDefinition,
): MasterPatternViewFieldDefinition[] {
  if (definition.sections) {
    return definition.sections.flatMap((s) => s.fields);
  }
  return definition.fields ?? [];
}

// ----- Descriptor Types -----

export interface ListColumnDescriptor {
  fieldName: string;
  label: string;
  dataType: string;
  sortable: boolean;
  inputType: MasterPatternViewInputType;
}

export interface FormInputDescriptor {
  fieldName: string;
  label: string;
  inputType: MasterPatternViewInputType;
  isRequired: boolean;
  maxLength?: number;
  colSize?: number | { sm?: number; md?: number; lg?: number };
  options?: SelectOption[];
  lookupConfig?: MasterPatternViewLookupConfig;
}

export interface FilterControlDescriptor {
  fieldName: string;
  label: string;
  dataType: string;
  inputType: MasterPatternViewInputType;
  filterOperator: FilterOperator;
}

export interface QuickFilterDescriptor {
  fieldName: string;
  label: string;
  inputType: MasterPatternViewInputType;
}

export interface SortableFieldDescriptor {
  fieldName: string;
  label: string;
}

// ----- Input Type Inference -----

const DATA_TYPE_TO_INPUT: Record<string, MasterPatternViewInputType> = {
  string: 'text',
  number: 'number',
  decimal: 'decimal',
  boolean: 'boolean',
  date: 'date',
  guid: 'text',
  file: 'file',
};

/** Always-sortable field names (code and name per project invariant). */
const ALWAYS_SORTABLE = new Set(['code', 'name']);

/** Maximum number of quick filters. */
const MAX_QUICK_FILTERS = 3;

const COL_SPAN_MAP: Record<number, string> = {
  1: 'col-span-1', 2: 'col-span-2', 3: 'col-span-3', 4: 'col-span-4',
  5: 'col-span-5', 6: 'col-span-6', 7: 'col-span-7', 8: 'col-span-8',
  9: 'col-span-9', 10: 'col-span-10', 11: 'col-span-11', 12: 'col-span-12',
};

/**
 * Resolves the Tailwind col-span class for a field based on its colSize definition.
 * Respects responsive colSize objects (uses md breakpoint, then lg, then sm).
 * Defaults to col-span-12 for textarea, col-span-6 for everything else.
 */
export function computeColSpanClass(
  field: Pick<MasterPatternViewFieldDefinition, 'colSize' | 'inputType' | 'dataType'>
): string {
  const inputType = field.inputType ?? inferInputType(field);
  const rawColSize = field.colSize;
  const colSizeNum: number =
    typeof rawColSize === 'object' && rawColSize !== null
      ? (rawColSize.md ?? rawColSize.lg ?? rawColSize.sm ?? (inputType === 'textarea' ? 12 : 6))
      : ((rawColSize as number | undefined) ?? (inputType === 'textarea' ? 12 : 6));
  return COL_SPAN_MAP[colSizeNum] ?? 'col-span-6';
}

/**
 * Resolves the input type for a field.
 * If `inputType` is explicitly set, returns it. Otherwise infers from `dataType`.
 */
export function inferInputType(
  field: Pick<MasterPatternViewFieldDefinition, 'dataType' | 'inputType'>
): MasterPatternViewInputType {
  if (field.inputType) {
    return field.inputType;
  }
  return DATA_TYPE_TO_INPUT[field.dataType] ?? 'text';
}

/**
 * Validates field definitions for mutual exclusivity violations.
 * Warns when `isOverridable` and `isCompanyOnly` are both `true` on the same field.
 * Per PRD §4.3 these flags are mutually exclusive.
 */
export function validateFieldDefinitions(
  fields: MasterPatternViewFieldDefinition[]
): void {
  for (const field of fields) {
    if (field.isOverridable === true && field.isCompanyOnly === true) {
      console.warn(
        `[MasterPatternView] Field "${field.fieldName}" has both isOverridable and isCompanyOnly set to true. ` +
          `These are mutually exclusive (PRD §4.3). isCompanyOnly will take precedence.`
      );
    }
  }
}

/**
 * Derives list column descriptors from field definitions.
 * Only includes fields with `showInList: true`.
 * Column order: explicit `listOrder` if any field defines it; otherwise preserves array declaration order.
 * `code` and `name` are always sortable.
 */
export function deriveListColumns(
  fields: MasterPatternViewFieldDefinition[]
): ListColumnDescriptor[] {
  const listFields = fields.filter((f) => f.showInList === true);
  const hasExplicitOrder = listFields.some((f) => f.listOrder !== undefined);
  if (hasExplicitOrder) {
    listFields.sort((a, b) => (a.listOrder ?? Infinity) - (b.listOrder ?? Infinity));
  }
  return listFields
    .map((f) => ({
      fieldName: f.fieldName,
      label: f.label,
      dataType: f.dataType,
      sortable: ALWAYS_SORTABLE.has(f.fieldName) || f.sortable === true,
      inputType: inferInputType(f),
    }));
}

/**
 * Derives form input descriptors from field definitions.
 * Only includes fields with `showInForm: true`, optionally filtered by tab.
 * Field order within each section is controlled by `formOrder` (independent of `listOrder`).
 */
export function deriveFormInputs(
  fields: MasterPatternViewFieldDefinition[],
  tab?: string
): FormInputDescriptor[] {
  return fields
    .filter((f) => {
      if (f.showInForm !== true) return false;
      if (tab !== undefined && f.formTab !== tab) return false;
      return true;
    })
    .sort((a, b) => (a.formOrder ?? Infinity) - (b.formOrder ?? Infinity))
    .map((f) => ({
      fieldName: f.fieldName,
      label: f.label,
      inputType: inferInputType(f),
      isRequired: f.isRequired === true,
      maxLength: f.maxLength,
      colSize: f.colSize,
      options: f.options,
      lookupConfig: f.lookupConfig,
    }));
}

/**
 * Derives filter control descriptors from field definitions.
 * Only includes fields with `filterable: true`.
 */
export function deriveFilterControls(
  fields: MasterPatternViewFieldDefinition[]
): FilterControlDescriptor[] {
  return fields
    .filter((f) => f.filterable === true)
    .map((f) => ({
      fieldName: f.fieldName,
      label: f.label,
      dataType: f.dataType,
      inputType: inferInputType(f),
      filterOperator: getDefaultFilterOperator(f.dataType),
    }));
}

/**
 * Derives quick filter descriptors (max 3) from field definitions.
 * Only includes fields with `quickFilter: true`.
 */
export function deriveQuickFilters(
  fields: MasterPatternViewFieldDefinition[]
): QuickFilterDescriptor[] {
  return fields
    .filter((f) => f.quickFilter === true)
    .slice(0, MAX_QUICK_FILTERS)
    .map((f) => ({
      fieldName: f.fieldName,
      label: f.label,
      inputType: inferInputType(f),
    }));
}

/**
 * Derives all sortable fields, always including `code` and `name`.
 * Used by the SortDropdown component.
 */
export function deriveSortableFields(
  fields: MasterPatternViewFieldDefinition[]
): SortableFieldDescriptor[] {
  const seen = new Set<string>();
  const result: SortableFieldDescriptor[] = [];

  // First add code and name unconditionally (if they exist in fields)
  for (const fieldName of ALWAYS_SORTABLE) {
    const field = fields.find((f) => f.fieldName === fieldName);
    if (field && !seen.has(fieldName)) {
      seen.add(fieldName);
      result.push({ fieldName: field.fieldName, label: field.label });
    }
  }

  // Then add other sortable fields
  for (const field of fields) {
    if (field.sortable === true && !seen.has(field.fieldName)) {
      seen.add(field.fieldName);
      result.push({ fieldName: field.fieldName, label: field.label });
    }
  }

  return result;
}

// ----- Internal Helpers -----

function getDefaultFilterOperator(dataType: string): FilterOperator {
  switch (dataType) {
    case 'string':
    case 'guid':
      return 'contains';
    default:
      return 'equals';
  }
}
